import { useRef, useEffect, useCallback, useState } from 'react';
import { useParams, useNavigate, useSearch } from '@tanstack/react-router';
import { SimulationForm, type SimulationFormHandle } from '@/components/form/SimulationForm';
import { useSimulation, useInvalidateSimulation } from '@/hooks/queries/useSimulationQueries';
import { useFormCacheSync, useActiveTeamSearch } from '@/hooks';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { createFormSyncEvents, FormSyncContext } from '@/hooks/formSyncEvents';
import { useLayoutContext } from '@/contexts/useLayoutContext';
import { simulationResponseToFormValues, reorderDeliverableResults } from '@/lib/utils';
import { runSimulation, getSimulationResult } from '@/lib/api';
import type { OrganizationMonteCarloResponse, SimulationFormValues } from '@/types/api';

// Empty form values (used when no simulation data is loaded yet)
const emptyFormValues: SimulationFormValues = {
  name: '',
  description: '',
  features: [],
  teams: [],
  num_simulations: 1000,
  start_date: new Date().toISOString().slice(0, 10),
  seed: '',
};

export function ConfigPage() {
  const { simulationId } = useParams({ from: '/simulation/$simulationId' });
  const currentSearch = useSearch({ from: '/simulation/$simulationId' });
  const navigate = useNavigate();
  const { activeTeamIndex, focusDeliverableId, setActiveTeamIndex } = useActiveTeamSearch();
  const {
    setSelectedSimulation,
    setResultsInfo,
    isSimulationRunning,
    simulationRunError,
    markSimulationRunStarted,
    clearSimulationRun,
  } = useLayoutContext();

  const formRef = useRef<SimulationFormHandle>(null);
  const invalidateSimulation = useInvalidateSimulation();

  // Track previous running state to detect completion
  const wasRunningRef = useRef(false);

  const { handleSuccess } = useErrorHandler('Simulation');

  // Fetch simulation data (pre-loaded by route loader, instant from cache)
  const { data: simulationData, refetch } = useSimulation(simulationId);

  // Convert simulation response to form values
  const formDataFromCache = simulationData ? simulationResponseToFormValues(simulationData) : undefined;

  // Sync React Query cache to form with surgical updates, announcing each value
  // it writes so auto-save does not send it back (shipcast-d8r.40).
  const [formSyncEvents] = useState(createFormSyncEvents);
  useFormCacheSync(formRef, formDataFromCache, { onFieldSynced: formSyncEvents.emit });

  // Form data for local use
  const formData = formDataFromCache || emptyFormValues;

  // Update layout context when simulation data loads
  useEffect(() => {
    if (simulationData) {
      setSelectedSimulation(simulationId, simulationData.name);
    }
  }, [simulationData, simulationId, setSelectedSimulation]);

  // Handle simulation result — reorder, cache in sessionStorage, navigate to results
  const handleResult = useCallback(
    (newResult: OrganizationMonteCarloResponse) => {
      const teamOrder = formData.teams.map((team) => team.name);
      const teamDeliverableOrder = Object.fromEntries(
        formData.teams.map((team) => [team.name, team.deliverables.map((d) => d.name)]),
      );

      const reordered = reorderDeliverableResults(newResult.deliverables, teamOrder, teamDeliverableOrder);

      // Cache result in sessionStorage for instant display on ResultsPage.
      try {
        sessionStorage.setItem(
          `simulation-result-${simulationId}`,
          JSON.stringify({ ...newResult, deliverables: reordered }),
        );
      } catch {
        // Silently ignore - ResultsPage falls back to API fetch
      }

      // Update layout context
      setResultsInfo(true, reordered.length);

      // Navigate to results (preserve search params like view, teams, features)
      navigate({ to: '/simulation/$simulationId/results', params: { simulationId }, search: currentSearch });
    },
    [formData, simulationId, setResultsInfo, navigate, currentSearch],
  );

  // Watch for simulation run completion (transition from running → not running)
  useEffect(() => {
    if (wasRunningRef.current && !isSimulationRunning) {
      // Run just finished
      if (!simulationRunError) {
        // Success — fetch the cached result and prepare it (but don't navigate)
        getSimulationResult(simulationId).then((cached) => {
          if (cached) {
            // Reorder results to match current form order
            const teamOrder = formData.teams.map((team) => team.name);
            const teamDeliverableOrder = Object.fromEntries(
              formData.teams.map((team) => [team.name, team.deliverables.map((d) => d.name)]),
            );
            const reordered = reorderDeliverableResults(cached.deliverables, teamOrder, teamDeliverableOrder);

            // Cache result in sessionStorage for instant display when user navigates to results
            try {
              sessionStorage.setItem(
                `simulation-result-${simulationId}`,
                JSON.stringify({ ...cached, deliverables: reordered }),
              );
            } catch {
              // Silently ignore - ResultsPage falls back to API fetch
            }

            // Update layout context (enables Results tab, shows result count in header)
            setResultsInfo(true, reordered.length);

            // Show success toast instead of navigating
            handleSuccess('Simulation completed successfully');
          }
        });
      } else {
        // Error — log it (the error is already visible in simulationRunError state)
        console.error('[ConfigPage] Simulation run failed:', simulationRunError);
      }
    }
    wasRunningRef.current = isSimulationRunning;
  }, [isSimulationRunning, simulationRunError, simulationId, formData, setResultsInfo, handleSuccess]);

  // Handle running the simulation (async — returns 202 immediately)
  // State is set optimistically BEFORE the API call to avoid a race condition:
  // fast simulations can complete via SSE before the HTTP 202 response arrives.
  const handleRunPersistent = async (formValues: SimulationFormValues, numSimulations: number) => {
    markSimulationRunStarted(simulationId);
    try {
      await runSimulation(simulationId, {
        num_simulations: numSimulations,
        start_date_override: formValues.start_date || null,
        seed_override: formValues.seed ? parseInt(formValues.seed, 10) : null,
      });
    } catch (error) {
      clearSimulationRun();
      throw error;
    }
  };

  // Handle refresh - invalidate cache, refetch, and reset form with fresh data
  const handleRefresh = useCallback(async () => {
    // Invalidate the cache to ensure we get fresh data
    await invalidateSimulation(simulationId);
    // Refetch the simulation data
    const { data: freshData } = await refetch();
    // Reset the form with the fresh data
    if (freshData && formRef.current) {
      const freshFormValues = simulationResponseToFormValues(freshData);
      formRef.current.reset(freshFormValues);
    }
  }, [invalidateSimulation, simulationId, refetch]);

  // Route loader guarantees simulationData is available
  if (!simulationData) {
    return null; // Should never happen - loader handles 404
  }

  return (
    <FormSyncContext.Provider value={formSyncEvents}>
      <SimulationForm
        ref={formRef}
        onResult={handleResult}
        defaultValues={formData}
        onFormDataChange={() => {}} // Form data now derived from React Query cache
        simulationId={simulationId}
        onRunPersistent={handleRunPersistent}
        onRefresh={handleRefresh}
        activeTeamIndex={activeTeamIndex}
        onActiveTeamIndexChange={setActiveTeamIndex}
        isSimulationRunning={isSimulationRunning}
        focusDeliverableId={focusDeliverableId}
      />
    </FormSyncContext.Provider>
  );
}
